import { renderLikeButton } from "./utils/render-like-button";
import renderCategories from "./utils/render-categories";
import renderPopupPhotoData from "./utils/render-popup-photo-data";
import { PhotoData } from "../photo/interfaces/dto";
import { generatePhotoData } from "./utils/generate-photo-data";
import syncInlineEditing from "./utils/sync-inline-editing";
import renderComments from "./utils/render-comments";
import { setupMetadataHover } from "../photo/setup-metadata-hover";
import resizePopupPhotoWrapper from "./utils/resize-popup-photo-wrapper";
import resizeShimmer from "./utils/resize-shimmer";
import { PhotoPopupData, PredefinedPopupData } from "./interfaces/dto";
import generatePopupData from "./utils/generate-popup-data";

declare var pm: PmInterface;

const { findHtmlEl, findHtmlEls, findImgEl, getClosest, emptyElement } =
	pm.utils.dom;

const popupClassName = "photo-popup";

const overlayClassName = popupClassName + "-overlay";

const openedClassName = "has-" + popupClassName + "-opened";

const triggerClassName = popupClassName + "-trigger";

const popupSelector = "." + popupClassName;

const overlaySelector = "." + overlayClassName;

const triggerSelector = "." + triggerClassName;

const gridImageClassName = "grid-image";

const gridImageSelector = "." + gridImageClassName;

const gridImgWrapperClassName = gridImageClassName + "-wrapper";

const gridImgWrapperSelector = "." + gridImgWrapperClassName;

/**
 * The popup overlay element.
 *
 * @var HTMLElement
 */
const overlay = findHtmlEl(overlaySelector);

/**
 * The last history state id.
 *
 * @var number
 */
let lastStateId = 0;

/**
 * Whether to force go back or not.
 *
 * @var boolean
 */
let forceGoBack = false;

const $overlay = jQuery(overlaySelector);

function generateStateId() {
	return new Date().getTime();
}

const popupMarkupToClone = findHtmlEl(popupSelector);

const prevNavButton = findHtmlEl(".popup-prev-button");

const nextNavButton = findHtmlEl(".popup-next-button");

const popupToClone = popupMarkupToClone?.cloneNode(true) as HTMLElement | null;

const commentListItemMarkupToClone = popupToClone
	? findHtmlEl(popupToClone, ".comment-list-item")
	: null;

const commentListItemToClone = commentListItemMarkupToClone?.cloneNode(
	true,
) as HTMLElement | null;

if (commentListItemMarkupToClone) {
	// Remove the markup (that was for cloning purpose) from the DOM.
	commentListItemMarkupToClone.parentNode?.removeChild(
		commentListItemMarkupToClone,
	);
}

if (popupMarkupToClone) {
	// Remove the markup (that was for cloning purpose) from the DOM.
	popupMarkupToClone.parentNode?.removeChild(popupMarkupToClone);
}

export default function setupPhotoPopup() {
	let predefinedData: PredefinedPopupData | null = null;
	let clickTrigger: HTMLElement | null = null;
	let popup: HTMLElement | null = null;
	let photoData: PhotoData | null = null;
	let popupData: PhotoPopupData | null = null;

	let gridPhoto: HTMLImageElement | null = null;
	let gridPhotoWrapper: HTMLElement | null = null;

	let nextDisabled = false;
	let prevDisabled = false;

	function resetPopupData() {
		predefinedData = null;
		clickTrigger = null;
		popup = null;
		photoData = null;
		popupData = null;

		gridPhoto = null;
		gridPhotoWrapper = null;

		nextDisabled = false;
		prevDisabled = false;
	}

	init();

	function init() {
		if (!overlay || !popupToClone || !commentListItemToClone) return;

		const initialStateId = generateStateId();

		window.history.replaceState(
			{ stateId: initialStateId, currentUrl: window.location.href },
			"",
			window.location.href,
		);

		lastStateId = initialStateId;

		document.addEventListener("DOMContentLoaded", handleDocumentReady);
		document.addEventListener("click", handleDocumentClick);
		document.addEventListener("keyup", handleDocumentKeyUp);
		window.addEventListener("popstate", handleWindowPopState);
		window.addEventListener("resize", handleWindowResize);

		if (prevNavButton) {
			prevNavButton.addEventListener("click", handlePrevButtonClick);
		}

		if (nextNavButton) {
			nextNavButton.addEventListener("click", handleNextButtonClick);
		}

		if (overlay) {
			overlay.addEventListener("click", closePopupOnOverlayClick);
			setupMetadataHover(overlay);
		}
	}

	function isPopupOpened() {
		return document.body.classList.contains(openedClassName);
	}

	function handlePrevButtonClick(_e: MouseEvent) {
		if (!isPopupOpened()) return;
		handleNextPrev("prev");
	}

	function handleNextButtonClick(_e: MouseEvent) {
		if (!isPopupOpened()) return;
		handleNextPrev("next");
	}

	function handleWindowResize(_e: Event) {
		if (!isPopupOpened()) return;
		if (!popup) return;

		if (photoData) resizePopupPhotoWrapper(popup, photoData);
		if (photoData) resizeShimmer(popup, photoData);
	}

	function handleDocumentReady(_e: Event) {
		const autoOpenEl = findHtmlEl(".auto-open-photo");
		if (!autoOpenEl) return;

		if (!autoOpenEl.classList.contains(gridImgWrapperClassName)) {
			return;
		}

		gridPhotoWrapper = autoOpenEl;

		gridPhoto = findImgEl(autoOpenEl, gridImageSelector);
		if (!gridPhoto) return;

		if (!gridPhoto.classList.contains(triggerClassName)) {
			return;
		}

		clickTrigger = gridPhoto;

		photoData = generatePhotoData(
			getPhotoIdFromDataset(gridPhotoWrapper),
			gridPhotoWrapper,
			gridPhoto,
		);

		predefinedData = null;
		popupData = null;

		openPopup();
	}

	function handleDocumentClick(e: MouseEvent) {
		const target = e.target;
		if (!(target instanceof Element)) return;

		clickTrigger = getClosest(target, triggerSelector);
		if (!(clickTrigger instanceof HTMLElement)) return;

		if (clickTrigger.tagName.toLowerCase() === "a") {
			e.preventDefault();
		}

		if (clickTrigger.dataset.predefinedPopupData) {
			// @ts-ignore
			predefinedData = window[clickTrigger.dataset.predefinedPopupData];

			photoData = predefinedData ? predefinedData.photoData : null;
			popupData = predefinedData ? predefinedData.popupData : null;

			gridPhoto = null;
			gridPhotoWrapper = null;
		} else if (clickTrigger instanceof HTMLImageElement) {
			predefinedData = null;
			gridPhoto = clickTrigger;
			gridPhotoWrapper = clickTrigger.parentElement;

			if (gridPhotoWrapper) {
				photoData = generatePhotoData(
					getPhotoIdFromDataset(gridPhotoWrapper),
					gridPhotoWrapper,
					gridPhoto,
				);
			}

			popupData = null;
		}

		openPopup();
	}

	function getPhotoIdFromDataset(target: HTMLElement): number {
		return target.dataset.photoId ? parseInt(target.dataset.photoId, 10) : 0;
	}

	function handleDocumentKeyUp(e: KeyboardEvent) {
		if (!e || !e.key) return;
		const key = e.key.toLowerCase();

		if (key === "escape" || key === "esc") {
			closePopupOnEscapeKeyPressed();
		} else if (
			key === "arrowleft" ||
			key === "left" ||
			key === "arrowright" ||
			key === "right"
		) {
			const direction = key === "arrowleft" || key === "left" ? "prev" : "next";

			const target = e.target;

			if (target instanceof Element) {
				// Return if user is typing a comment or doing inline-editing.
				if (
					target.tagName.toLowerCase() === "textarea" ||
					target.tagName.toLowerCase() === "input"
				) {
					return;
				}
			}

			handleNextPrev(direction);
		}
	}

	function handleWindowPopState(e: PopStateEvent) {
		const currentStateId: number | null = e.state ? e.state.stateId : null;

		if (!currentStateId) {
			if (document.body.classList.contains(openedClassName)) {
				if (forceGoBack) {
					window.history.back();
					return;
				}
			}

			return;
		}

		forceGoBack = false;

		const direction =
			currentStateId === lastStateId
				? "none"
				: currentStateId < lastStateId
					? "back"
					: "forward";
		lastStateId = currentStateId;

		if (direction === "none") {
			if (document.body.classList.contains(openedClassName)) {
				// console.log(`The direction is none let's go back one more time`);

				window.history.back();
			}
		}

		if (direction === "back") {
			if (document.body.classList.contains(openedClassName)) {
				// console.log(`The direction is back let's close the popup`);

				closePopup();
				return;
			}

			return;
		}

		const nextPhotoId =
			e.state && e.state.popupPhotoId ? e.state.popupPhotoId : 0;
		if (!nextPhotoId) return;

		const nextGridPhotoWrapper = findHtmlEl(
			`${gridImgWrapperSelector}[data-photo-id="${nextPhotoId}"]`,
		);
		if (!nextGridPhotoWrapper) return;

		const nextGridPhoto = findImgEl(nextGridPhotoWrapper, gridImageSelector);
		if (!nextGridPhoto) return;

		gridPhotoWrapper = nextGridPhotoWrapper;
		gridPhoto = nextGridPhoto;

		photoData = generatePhotoData(
			getPhotoIdFromDataset(gridPhotoWrapper),
			gridPhotoWrapper,
			gridPhoto,
		);

		popupData = null;

		openPopupByForwardEvent();
	}

	function openPopupByForwardEvent() {
		forceGoBack = false;

		const popup = clonePopup();
		document.title = "Loading...";

		$overlay
			.stop()
			.css({
				display: "block",
			})
			.animate({ opacity: 1 }, 250, function () {
				document.title = gridPhoto
					? gridPhoto.alt + " - " + pm.pageTitle
					: pm.pageTitle;
				document.body.classList.add(openedClassName);
				if (photoData) resizePopupPhotoWrapper(popup, photoData);
				if (photoData) resizeShimmer(popup, photoData);
				if (prevNavButton) prevNavButton.classList.remove("is-hidden");
				if (nextNavButton) nextNavButton.classList.remove("is-hidden");
				popup.style.opacity = "1";
			});
	}

	function openPopup() {
		forceGoBack = false;

		popup = clonePopup();
		const newStateId = generateStateId();

		window.history.pushState(
			{
				stateId: newStateId,
				currentUrl: window.location.href,
				popupPhotoId: photoData ? photoData.id : 0,
			},
			"",
			photoData ? photoData.attachmentUrl : "",
		);

		lastStateId = newStateId;
		document.title = "Loading...";

		$overlay
			.stop()
			.css({
				display: "block",
			})
			.animate({ opacity: 1 }, 250, function () {
				document.title = gridPhoto
					? gridPhoto.alt + " - " + pm.pageTitle
					: pm.pageTitle;
				document.body.classList.add(openedClassName);
				if (popup && photoData) resizePopupPhotoWrapper(popup, photoData);
				if (popup && photoData) resizeShimmer(popup, photoData);
				if (prevNavButton) prevNavButton.classList.remove("is-hidden");
				if (nextNavButton) nextNavButton.classList.remove("is-hidden");
				if (popup) popup.style.opacity = "1";
			});
	}

	function clonePopup(): HTMLElement {
		const clonedPopup = popupToClone!.cloneNode(true) as HTMLElement;
		clonedPopup.dataset.photoId = photoData ? photoData.id.toString() : "";
		clonedPopup.style.opacity = "0";

		const predefinedPopupDataEls = findHtmlEls(
			clonedPopup,
			"[data-predefined-popup-data]",
		);

		if (predefinedPopupDataEls.length) {
			// Loop using for loop.
			for (let i = 0; i < predefinedPopupDataEls.length; i++) {
				const predefinedPopupDataEl = predefinedPopupDataEls[i];

				if (clickTrigger && predefinedData) {
					predefinedPopupDataEl.dataset.predefinedPopupData =
						clickTrigger.dataset.predefinedPopupData;
				} else {
					predefinedPopupDataEl.removeAttribute("data-predefined-popup-data");
				}
			}
		}

		if (predefinedData) {
			nextDisabled = true;
			prevDisabled = true;
		} else {
			nextDisabled = false;
			prevDisabled = false;
		}

		if (prevDisabled) {
			disablePrev();
		} else {
			enablePrev();
		}

		if (nextDisabled) {
			disableNext();
		} else {
			enableNext();
		}

		if (photoData) {
			if (!popupData && gridPhotoWrapper) {
				popupData = generatePopupData(clonedPopup, photoData, gridPhotoWrapper);
			}

			const isVideo = photoData.videoUrl ? true : false;

			const popupPhoto = findImgEl(clonedPopup, "img.single-image");
			const videoIframe = findHtmlEl(clonedPopup, "iframe.single-image");

			if (isVideo) {
				popupPhoto?.parentElement?.removeChild(popupPhoto);
			} else {
				videoIframe?.parentElement?.removeChild(videoIframe);
			}

			const mediaEl = isVideo ? videoIframe : popupPhoto;

			mediaEl?.addEventListener("load", function (_e: Event) {
				handleOnMediaLoad(clonedPopup, mediaEl);
			});

			renderPopupPhotoData(clonedPopup, popupData!);
			renderCategories(clonedPopup, photoData.categories);

			syncInlineEditing(clonedPopup, photoData);
			renderComments(clonedPopup, photoData, commentListItemToClone!);
		}

		const likeButton = findHtmlEl(clonedPopup, ".like-photo");

		if (
			photoData &&
			(likeButton instanceof HTMLAnchorElement ||
				likeButton instanceof HTMLButtonElement)
		) {
			renderLikeButton(
				likeButton,
				photoData.id,
				photoData.isLiked,
				photoData.totalLikes,
			);
		}

		overlay!.appendChild(clonedPopup);
		clonedPopup.classList.remove("is-hidden");

		return clonedPopup;
	}

	function disablePrev() {
		if (!prevNavButton) return;
		prevNavButton.style.display = "none";
	}

	function disableNext() {
		if (!nextNavButton) return;
		nextNavButton.style.display = "none";
	}

	function enablePrev() {
		if (!prevNavButton) return;
		prevNavButton.style.display = "block";
	}

	function enableNext() {
		if (!nextNavButton) return;
		nextNavButton.style.display = "block";
	}

	function handleOnMediaLoad(popup: HTMLElement, mediaEl: Element) {
		const shimmer = findHtmlEl(popup, ".loading-shimmer");
		if (shimmer) shimmer.classList.add("is-hidden");

		mediaEl.classList.remove("is-loading");
		popup.classList.remove("is-reloading");
	}

	/**
	 * Close the popup on overlay click.
	 */
	function closePopupOnOverlayClick(e: MouseEvent) {
		const target = e.target;
		if (!(target instanceof Element)) return;
		if (!target.classList.contains(overlayClassName)) return;

		forceGoBack = true;
		window.history.back();
	}

	/**
	 * Close the popup on escape key pressed.
	 */
	function closePopupOnEscapeKeyPressed() {
		if (findHtmlEl(popupSelector + " .inline-editing.is-editing")) {
			return;
		}

		if (!isPopupOpened()) return;
		forceGoBack = true;
		window.history.back();
	}

	/**
	 * Close the popup.
	 */
	function closePopup() {
		// Prevent conflict with context menu exit process (or maybe even other processes).
		if (!document.body.classList.contains(openedClassName)) {
			return;
		}

		forceGoBack = false;
		document.title = pm.pageTitle;

		if (prevNavButton) prevNavButton.classList.add("is-hidden");
		if (nextNavButton) nextNavButton.classList.add("is-hidden");

		$overlay.stop().animate({ opacity: 0 }, 250, function () {
			overlay!.scrollTo(0, 0);
			$overlay.empty();
			overlay!.style.display = "none";
			document.body.classList.remove(openedClassName);

			resetPopupData();
		});
	}

	/**
	 * Handle arrow key pressed.
	 *
	 * @param direction Direction of the arrow key pressed (left or right).
	 */
	function handleNextPrev(direction: string) {
		if (!document.body.classList.contains(openedClassName)) return;
		if (!popup) return;

		let gridToShow: Element | null | undefined = null;

		if (gridPhotoWrapper) {
			if ("prev" === direction) {
				gridToShow = gridPhotoWrapper.parentElement?.previousElementSibling;
			} else {
				gridToShow = gridPhotoWrapper.parentElement?.nextElementSibling;
			}
		}

		if (!(gridToShow instanceof HTMLElement)) return;

		const gridPhotoWrapperToShow = findHtmlEl(
			gridToShow,
			gridImgWrapperSelector,
		);
		if (!gridPhotoWrapperToShow) return;

		const gridPhotoToShow = findImgEl(
			gridPhotoWrapperToShow,
			gridImageSelector,
		);
		if (!gridPhotoToShow) return;

		const photoIdToShow: number = getPhotoIdFromDataset(gridPhotoWrapperToShow);
		if (!photoIdToShow) return;

		gridPhoto = gridPhotoToShow;
		gridPhotoWrapper = gridPhotoWrapperToShow;

		photoData = generatePhotoData(photoIdToShow, gridPhotoWrapper, gridPhoto);

		popupData = null;

		replacePopup();
	}

	function replacePopup() {
		popup = clonePopup();
		const newStateId = generateStateId();

		window.history.replaceState(
			{
				stateId: newStateId,
				currentUrl: window.location.href,
				popupPhotoId: photoData ? photoData.id : 0,
			},
			"",
			photoData ? photoData.attachmentUrl : "",
		);

		lastStateId = newStateId;
		document.title = "Loading...";

		popup.style.opacity = "0";
		emptyElement(overlay);

		overlay?.appendChild(popup);

		document.title = gridPhoto
			? gridPhoto.alt + " - " + pm.pageTitle
			: pm.pageTitle;
		if (photoData) resizePopupPhotoWrapper(popup, photoData);
		if (photoData) resizeShimmer(popup, photoData);

		popup.style.opacity = "1";
	}
}
